/**
 * Next reminder card (design D, right column of the invoice detail): when the
 * next automatic reminder goes out and which template it uses, or why none
 * will, with Pause / Resume. The copy comes from recurringModel's
 * nextReminderView so it is tested without rendering.
 */
import React, { useState } from 'react';
import InvoiceRepository, { InvoiceDoc, InvoiceSettings } from '../../../internals/repository/InvoiceRepository';
import { nextReminderView } from '../recurringModel';
import { errorText } from '../detailModel';

const TEMPLATE_TAG: Record<'reminder' | 'overdue', { label: string; cls: string; fg: string }> = {
  reminder: { label: 'Payment reminder', cls: 'bg-brand-soft', fg: 'var(--brand)' },
  overdue: { label: 'Overdue notice', cls: 'bg-warn-soft', fg: 'var(--warnFg)' },
};

export default function NextReminderCard({
  doc, settings, shopId, onChanged,
}: {
  doc: InvoiceDoc;
  settings?: InvoiceSettings | null;
  shopId: string;
  /** Called after a pause or resume; reload the document. */
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const v = nextReminderView(doc, settings);

  const toggle = async () => {
    if (busy || !v.action) return;
    setBusy(true);
    setError(null);
    try {
      await InvoiceRepository.setRemindersPaused(shopId, doc.docId, v.action === 'pause');
      onChanged();
    } catch (e: any) {
      setError(errorText(e, { invalid_state: 'Reminders can only be changed on an open invoice.' }));
    } finally {
      setBusy(false);
    }
  };

  const tag = v.template ? TEMPLATE_TAG[v.template] : null;

  return (
    <section className="nl-card flex flex-col gap-2 px-[18px] py-3.5" aria-label="Reminders">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="font-semibold text-text1">{v.title}</span>
          {v.when && <span className="nl-num text-[14px] font-semibold text-text1">{v.when}</span>}
          {tag && (
            <span
              className={`inline-flex h-5 w-fit items-center rounded-md px-1.5 text-[11px] font-semibold ${tag.cls}`}
              style={{ color: tag.fg }}
            >
              {tag.label}
            </span>
          )}
          <span className="text-[13px] text-text2">{v.line}</span>
        </div>
        {v.action && (
          <button
            type="button"
            className="nl-btn nl-btn--secondary nl-btn--sm shrink-0"
            onClick={toggle}
            disabled={busy}
            aria-label={v.action === 'pause' ? 'Pause reminders' : 'Resume reminders'}
          >
            {busy ? '…' : v.action === 'pause' ? 'Pause' : 'Resume'}
          </button>
        )}
      </div>
      {error && <span role="alert" className="text-[12px]" style={{ color: 'var(--negFg)' }}>{error}</span>}
    </section>
  );
}
